'use client'

import { DUR } from '@/animations/settings/vars'
import { usePrefersReducedMotion } from '@/hooks'
import { cn } from '@/lib/utils'
import { Typo3SectionSpotHeadlessHero } from '@/types'
import { Div, Img } from '@/ui/components'
import type { APITypes } from '@/ui/components/media/PlyrClient'
import { MEDIA } from '@spot'
import dynamic from 'next/dynamic'
import { PlyrProps } from 'plyr-react'
import {
  type ForwardRefExoticComponent,
  type RefAttributes,
  memo,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState
} from 'react'

const PlyrComponent = dynamic(
  async () => (await import('@/ui/components/media/PlyrClient')).default
) as ForwardRefExoticComponent<PlyrProps & RefAttributes<APITypes>>

const Slider = dynamic(
  async () => await import('@/ui/components/Slider/Slider')
)

const Slide = dynamic(async () => await import('@/ui/components/Slider/Slide'))

interface Props {
  data: Typo3SectionSpotHeadlessHero
}

const applyPlayerState = (player: Plyr, enabled: boolean) => {
  if (enabled) {
    player.muted = true
    player.loop = true
    const playPromise = player.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => null)
    }
  } else {
    player.loop = false
    player.pause()
    player.currentTime = 0
  }
}

const getProvider = (provider?: string): Plyr.Provider => {
  if (!provider) {
    return 'html5'
  }
  if (provider.includes('youtube')) {
    return 'youtube'
  } else if (provider.includes('vimeo')) {
    return 'vimeo'
  }

  return 'html5'
}

const SlideImage = ({ data }: Props) => {
  const media = useMemo(() => data.images ?? [], [data.images])
  const prefersReducedMotion = usePrefersReducedMotion()
  const [isVideoEnabled, setIsVideoEnabled] = useState(!prefersReducedMotion)
  const [userOverrode, setUserOverrode] = useState(false)
  const plyrRef = useRef<APITypes | null>(null)
  const [isPlayerReady, setIsPlayerReady] = useState(false)

  const video = useMemo(
    () => media.find((item) => item.properties?.type === 'video'),
    [media]
  )

  const videoSrc = video?.publicUrl as string | undefined

  const plyrSource = useMemo<PlyrProps['source']>(() => {
    if (!videoSrc) {
      return null
    }
    return {
      type: 'video',
      sources: [
        {
          src: videoSrc,
          provider: getProvider(video?.properties?.extension)
        }
      ]
    }
  }, [videoSrc, video?.properties?.extension])

  const plyrOptions = useMemo<PlyrProps['options']>(
    () => ({
      autoplay: !prefersReducedMotion,
      muted: true,
      loop: {
        active: !prefersReducedMotion
      },
      volume: 0,
      controls: [
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'fullscreen'
      ]
    }),
    [prefersReducedMotion]
  )

  useEffect(
    () => () => {
      const player = plyrRef.current?.plyr
      try {
        player?.destroy()
      } catch {
        // Plyr can throw while tearing down provider (e.g. YouTube) internals
      }
    },
    []
  )

  const onSetIsVideoEnabled = useEffectEvent((enabled: boolean) => {
    setIsVideoEnabled(enabled)
  })

  const onSetUserOverrode = useEffectEvent((overrode: boolean) => {
    setUserOverrode(overrode)
  })

  useEffect(() => {
    if (prefersReducedMotion) {
      onSetIsVideoEnabled(false)
      onSetUserOverrode(false)
    } else if (!userOverrode) {
      onSetIsVideoEnabled(true)
    }
  }, [prefersReducedMotion, userOverrode])

  const onSetIsPlayerReady = useEffectEvent((ready: boolean) => {
    setIsPlayerReady(ready)
  })

  useEffect(() => {
    const player = plyrRef.current?.plyr
    if (!player || !player?.source) {
      return
    }

    // Set ready state when player is available
    if (!isPlayerReady) {
      onSetIsPlayerReady(true)
    }

    applyPlayerState(player, isVideoEnabled)
  }, [isVideoEnabled, isPlayerReady])

  if (media.length === 0) {
    return null
  }

  const containerClasses = cn('pl-padding-lg w-full')

  if (video) {
    return (
      <div className={cn(containerClasses, 'aspect-rect md:aspect-1392/655')}>
        <PlyrComponent
          key={videoSrc}
          ref={plyrRef}
          source={plyrSource}
          options={plyrOptions}
        />
        {(prefersReducedMotion || !isVideoEnabled) && (
          <div className="absolute right-6 bottom-6 z-2 flex flex-col items-center gap-3 rounded-2xl bg-black/60 px-6 py-4 text-center text-white">
            {!isVideoEnabled && (
              <p className="m-0 text-sm">
                Background video is paused to reduce motion.
              </p>
            )}
            <button
              type="button"
              className="cursor-pointer appearance-none rounded-full border border-white bg-transparent px-6 py-2 text-sm text-white transition-[background-color,color,border-color] duration-(--duration-default) ease-(--ease-out-cubic) hover:border-white hover:bg-white hover:text-black focus-visible:border-white focus-visible:bg-white focus-visible:text-black"
              onClick={() => {
                setUserOverrode(true)
                setIsVideoEnabled((prev) => !prev)
              }}
              aria-pressed={isVideoEnabled}
            >
              {isVideoEnabled
                ? 'Pause background video'
                : 'Play background video'}
            </button>
            {video?.properties?.description && (
              <div
                className={cn(
                  'text-small',
                  'flex h-8 w-full justify-center pt-4 text-neutral-400'
                )}
              >
                {video?.properties?.description}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  if (media.length === 1) {
    const item = media[0]
    return (
      <div className={cn(containerClasses, 'aspect-rect md:aspect-1392/655')}>
        <Img
          data={item}
          fill={true}
          objectCover
          className="h-full w-full"
          sizes={`${MEDIA.tablet} 100vw, 84rem`}
          priority={true}
        />
        {item.properties?.description && (
          <div
            className={cn(
              'text-small',
              'flex h-8 w-full justify-center pt-4 text-neutral-400'
            )}
          >
            {item.properties?.description}
          </div>
        )}
      </div>
    )
  } else if (media.length > 1) {
    return (
      <Div
        className={containerClasses}
        anim="fade-in"
        animVars={{ delay: DUR.stagger * 2 }}
      >
        <Slider
          slidesPerView={1}
          spaceBetween="0"
          isContainer={false}
          sliderIsFade={true}
          showArrows={true}
          arrowPosition="bottom-right"
          sliderOptions={{
            loop: true,
            duration: 40
          }}
          //sliderAutoplay={true}
        >
          {media.map((item, index) => (
            <Slide
              key={index?.toString()}
              className="aspect-rect w-full md:aspect-1392/655"
            >
              <Img
                data={item}
                fill={true}
                objectCover
                className="h-full w-full"
                sizes="84rem"
                priority={index === 0 ? true : false}
              />
              <div
                className={cn(
                  'text-small',
                  'flex h-8 w-full justify-center pt-4 text-neutral-400'
                )}
              >
                {item.properties?.description}
              </div>
            </Slide>
          ))}
        </Slider>
      </Div>
    )
  }

  return null
}

export default memo(SlideImage)
